import { isTruthy } from '@app/utils/isTruthy'
import moment from '@app/utils/moment'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { configSelector } from './config'

const scriptsSlice = createReduxSlice<{ loaded_at: string }>('scripts')

const setScriptsLoaded = () => scriptsSlice.set({ loaded_at: moment().format() })

export const getScriptsLoaded = () =>
  createThunk(dispatch => {
    return !!dispatch(scriptsSlice.get())?.loaded_at
  })

export const processScripts = (worker: (script: ScriptDescriptor) => unknown) =>
  createThunk(async (dispatch, getState) => {
    const { metrikaCounterId } = configSelector(getState())
    const scripts = await getScripts(metrikaCounterId)
    for (const script of scripts) {
      if (script.disabled) continue
      if (!script.mode.includes(API_MODE)) continue
      await worker(script)
    }

    dispatch(setScriptsLoaded())
  })

const getScripts = async (metrikaCounterId: string | null) => {
  const [tagManager, metrika, vkPixel, vkPixelNn] = await Promise.all([
    import('@app/assets/third_party_scripts/tag_manager.txt?raw').then(m => m.default),
    import('@app/assets/third_party_scripts/metrika.txt?raw').then(m => m.default),
    import('@app/assets/third_party_scripts/vk_pixel.txt?raw').then(m => m.default),
    import('@app/assets/third_party_scripts/vk_pixel_nn.txt?raw').then(m => m.default),
  ])

  // Metrika is skipped entirely when its counter id isn't configured.
  const metrikaScript = metrikaCounterId ? metrika.replace(/%metrika_id%/g, metrikaCounterId) : null

  return [tagManager, metrikaScript, vkPixel, vkPixelNn].filter(isTruthy).map(parseScript)
}

type ScriptDescriptor = {
  /** Mode in which script should be executed */
  mode: string[]
  /** Whether script is disabled */
  disabled: boolean
  /** Parts that should be appended to head */
  head?: string
  /** Parts that should be appended to body */
  body?: string
}

const parseScript = (script: string): ScriptDescriptor => {
  const parts = script.split('\n---').map(p => p.trim())
  if (parts.length !== 3) throw new Error('Invalid script format')
  const header = parts[0]
  const headerParts = header.split('\n')
  const disabled = !!headerParts.find(p => p.trim().toLocaleLowerCase() === 'disabled')
  const modeRow = headerParts.find(p => p.startsWith('MODE:'))
  if (!modeRow) throw new Error('Missing mode for script')
  const modes = modeRow
    .split(':')[1]
    .split(',')
    .map(m => m.trim())
  if (!modes.length) throw new Error('No modes specified for script')
  const head = parts[1]
  const body = parts[2]
  return {
    mode: modes,
    disabled,
    head: head || undefined,
    body: body || undefined,
  }
}
